/**
 * OASIS Types and Interfaces
 */

import { AssessmentStatus } from '../../services/patientmr/assessments.service';

export enum OasisType {
  OASIS_SOC = 'OASIS_SOC',          // Start of Care
  OASIS_ROC = 'OASIS_ROC',          // Recertification
  OASIS_DISCHARGE = 'OASIS_DISCHARGE' // Discharge
}

// DEPRECATED: Use AssessmentStatus instead
// Keeping for backwards compatibility during migration
export enum OasisStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  QA = 'QA',
  COMPLETE = 'COMPLETE',
  LOCKED = 'LOCKED',
  QA_APPROVED = 'QA_APPROVED'
}

// Type alias for clarity - OASIS uses the same statuses as assessments
export type OasisStatusType = AssessmentStatus;

export interface OasisAssessment {
  assessmentId: string;
  patientId: string;
  tenantId: string;
  type: OasisType;
  assessmentDate: string;
  episodeStartDate?: string;
  episodeEndDate?: string;
  status: AssessmentStatus; // Changed from OasisStatus to AssessmentStatus
  hippsCode?: string;
  responses: OasisResponses;
  clinicianId: string;
  clinicianName: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  lockedAt?: string;
  approvedAt?: string;
  version: number;
}

export interface OasisResponses {
  // Administrative Items
  M0010_cmsNum?: string;
  M0014_branch?: string;
  M0016_patientId?: string;
  M0018_natHiNum?: string;
  M0020_patientName?: string;
  M0030_startCareDate?: string;
  M0032_resumeCareDate?: string;
  M0040_patientState?: string;
  M0050_patientZip?: string;
  M0060_patientAge?: number;
  M0063_inpatientFacility?: string;
  M0064_inpatientDischargeDate?: string;
  M0065_inpatientDiagnosisIcd?: string;
  M0066_inpatientDiagnosisDate?: string;
  M0069_inpatientProceduresIcd?: string;
  M0070_inpatientProceduresDate?: string;
  M0080_disciplineOrderedSoc?: string[];
  M0090_disciplineOrderedRoc?: string[];
  M0100_thisAssessmentCompleted?: string;
  M0102_physicianOrderedSoc?: string;
  M0104_physicianOrderedRoc?: string;
  M0110_episodeOfCare?: string;

  // Clinical Record Items
  M1000_inpatientDiagnoses?: string;
  M1002_inpatientProcedures?: string;
  M1004_inpatientMedications?: string;
  M1010_inpatientPrognosis?: string;
  M1012_inpatientRisk?: string;
  M1014_inpatientActivities?: string;
  M1016_inpatientMentalStatus?: string;
  M1018_inpatientOverallStatus?: string;

  // Diagnoses and Condition
  M1021_primaryDiagnosis?: string;
  M1023_primaryDiagnosisChange?: string;
  M1025_secondaryDiagnosis1?: string;
  M1027_secondaryDiagnosis2?: string;
  M1029_secondaryDiagnosis3?: string;
  M1031_secondaryDiagnosis4?: string;
  M1033_riskFactors?: string[];
  M1035_inpatientProcedures?: string;

  // Living Arrangements
  M1100_patientLiving?: string;
  M1110_patientLivingWith?: string[];

  // Sensory Status
  M1200_vision?: string;
  M1210_speechPatterns?: string;
  M1220_hearingPatterns?: string;
  M1230_speechClarity?: string;
  M1240_painAssessment?: string;
  M1242_painFrequency?: string;
  M1245_painInterference?: string;

  // Integumentary Status
  M1300_pressureUlcer?: string;
  M1302_pressureUlcerNumber?: number;
  M1306_pressureUlcerStage?: string;
  M1308_pressureUlcerStatus?: string;
  M1310_pressureUlcerLongest?: string;
  M1312_pressureUlcerProblematic?: string;
  M1320_statusProblematicUlcer?: string;
  M1322_currentNumberUlcers?: number;
  M1324_stage1PressureUlcers?: number;
  M1330_woundStasis?: string;
  M1332_woundSurgical?: string;
  M1334_woundOther?: string;
  M1340_woundStasisStatus?: string;
  M1342_woundSurgicalStatus?: string;
  M1350_skinLesions?: string;

  // Respiratory Status
  M1400_whenDyspneic?: string;
  M1410_respiratoryTreatments?: string[];

  // Cardiac Status
  M1500_heartFailure?: string;
  M1510_heartFailureFollowUp?: string;
  M1520_heartFailureCardiologist?: string;

  // Elimination Status
  M1600_urinaryTract?: string;
  M1610_urinaryIncontinence?: string;
  M1620_bowelIncontinence?: string;
  M1630_ostomy?: string;

  // Neuro/Emotional/Behavioral Status
  M1700_cognitiveFunction?: string;
  M1710_whenConfused?: string;
  M1720_whenAnxious?: string;
  M1730_depression?: string;
  M1740_cognitiveAssessment?: string;
  M1745_phq2?: string;
  M1750_patientMoodInterview?: string;

  // ADL/IADLs
  M1800_grooming?: string;
  M1810_currentDressing?: string;
  M1820_currentBathing?: string;
  M1830_currentToileting?: string;
  M1840_currentTransferring?: string;
  M1845_currentAmbulation?: string;
  M1850_currentFeeding?: string;
  M1860_currentMealPrep?: string;
  M1870_currentTransportation?: string;
  M1880_currentLaundry?: string;
  M1890_currentHousekeeping?: string;
  M1900_currentShopping?: string;
  M1910_currentTelephone?: string;

  // Medications
  M2000_drugRegimen?: string;
  M2002_medicationManagement?: string;
  M2004_medicationIntervention?: string;
  M2010_patientHighRisk?: string;
  M2020_managementOralMed?: string;
  M2030_managementInjection?: string;
  M2040_priorMedicationManagement?: string;

  // Care Management
  M2100_patientCareNeeds?: string;
  M2110_frequentMedChanges?: string;
  M2200_therapyNeedSoc?: string;
  M2250_planOfCareReview?: string;
  M2300_emergencyCarePlan?: string;
  M2400_interventionSynopsis?: string;
  M2410_interventionSynopsisHha?: string;
  M2420_dischargePlanning?: string;

  // Therapy Need and Plan of Care
  M2001_drugRegimen?: string;
  M2003_medicationManagement?: string;
  M2005_medicationIntervention?: string;
  M2015_patientHighRisk?: string;
  M2025_managementOralMed?: string;
  M2035_managementInjection?: string;
  M2045_priorMedicationManagement?: string;

  // Discharge items (for discharge assessments)
  M3000_dischargeTo?: string;
  M3005_dischargeDate?: string;
  M3010_dischargeReason?: string;
  M3015_dischargeContact?: string;
  M3020_emergencyUse?: string;
  M3025_emergencyUseAfter?: string;
  M3030_emergencyUseReasons?: string[];
}

export interface OasisFormData {
  patientId: string;
  assessmentDate: string;
  type: OasisType;
  episodeStartDate?: string;
  episodeEndDate?: string;
  responses: OasisResponses;
}

export interface OasisListItem {
  assessmentId: string;
  patientId: string;
  type: OasisType;
  assessmentDate: string;
  status: AssessmentStatus; // Changed from OasisStatus to AssessmentStatus
  hippsCode?: string;
  clinicianName: string;
  createdAt: string;
  updatedAt: string;
}

export interface HippsCodeCalculation {
  hippsCode: string;
  points: number;
  description: string;
  paymentAmount?: number;
  factors: {
    clinical: number;
    functional: number;
    service: number;
  };
}

export interface OasisValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface OasisValidationResult {
  isValid: boolean;
  errors: OasisValidationError[];
  warnings: OasisValidationError[];
  hippsCode?: string;
}

// OASIS field configuration for dynamic form rendering
export interface OasisFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'textarea' | 'icd-select';
  required: boolean;
  category: string;
  subcategory?: string;
  options?: Array<{
    value: string;
    label: string;
    description?: string;
  }>;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
  dependsOn?: string[];
  helpText?: string;
  oasisItem?: string; // Official OASIS item number
}

// Categories for organizing OASIS fields
export const OasisCategories = {
  ADMINISTRATIVE: 'Administrative',
  CLINICAL_RECORD: 'Clinical Record',
  DIAGNOSES: 'Diagnoses and Condition',
  LIVING_ARRANGEMENTS: 'Living Arrangements',
  SENSORY_STATUS: 'Sensory Status',
  INTEGUMENTARY: 'Integumentary Status',
  RESPIRATORY: 'Respiratory Status',
  CARDIAC: 'Cardiac Status',
  ELIMINATION: 'Elimination Status',
  NEURO_EMOTIONAL: 'Neuro/Emotional/Behavioral Status',
  ADL_IADL: 'ADL/IADLs',
  MEDICATIONS: 'Medications',
  CARE_MANAGEMENT: 'Care Management',
  THERAPY_PLAN: 'Therapy Need and Plan of Care',
  DISCHARGE: 'Discharge Items'
} as const;

export type OasisCategory = typeof OasisCategories[keyof typeof OasisCategories];
